import { toHtml } from 'hast-util-to-html';
import type { ElementContent } from 'hast';

import type { VirtualizationOptions } from '../types';

const DEFAULT_OPTIONS: Required<VirtualizationOptions> = {
  enabled: false,
  overscan: 10,
  lineHeight: 20,
  threshold: 500,
  containerClassName: '',
  containerStyle: {},
};

interface VisibleRange {
  start: number;
  end: number;
}

/**
 * Monaco-style virtualized rendering manager.
 *
 * Key optimizations:
 * 1. Lazy HTML conversion — HAST nodes are stored raw and only converted
 *    to HTML strings when they enter the viewport (via toHtml per-node).
 *    This avoids the O(N) cost of toHtml on the entire 5000-node AST.
 * 2. Batch innerHTML — visible lines are joined and set via one innerHTML
 *    per scroll frame.
 * 3. O(1) range calculation — uses scrollY math, not getBoundingClientRect.
 * 4. Position caching — container position measured on resize only.
 */
export class VirtualizationManager {
  private options: Required<VirtualizationOptions>;
  private container: HTMLElement | null = null;
  private contentWrapper: HTMLElement | null = null;
  private spacerTop: HTMLElement | null = null;
  private spacerBottom: HTMLElement | null = null;

  private totalLines: number = 0;

  /**
   * Per-line HTML strings. Populated eagerly (setupFromHTML/setupFromHTMLString)
   * or lazily from HAST nodes (setupFromHAST — null means not yet converted).
   */
  private lineHTMLs: (string | null)[] = [];

  /** HAST nodes for lazy conversion. Only used with setupFromHAST. */
  private hastNodes: (ElementContent | null)[] = [];

  private renderedRange: VisibleRange = { start: 0, end: 0 };
  private isVirtualized: boolean = false;
  private totalContentHeight: number = 0;
  private containerPageTop: number = 0;

  private rafId: number | null = null;
  private scrollHandler: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(options: VirtualizationOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  setOptions(options: VirtualizationOptions): void {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  isActive(): boolean {
    return this.isVirtualized;
  }

  shouldVirtualize(lineCount: number): boolean {
    return this.options.enabled && lineCount >= this.options.threshold;
  }

  private measureContainerPosition(): void {
    if (!this.container) return;
    const rect = this.container.getBoundingClientRect();
    this.containerPageTop = rect.top + window.scrollY;
  }

  /**
   * Setup from pre-parsed DOM elements (legacy API).
   */
  setup(
    codeElement: HTMLElement,
    lineElements: HTMLElement[],
    _scrollContainer: HTMLElement
  ): void {
    const htmls: string[] = new Array(lineElements.length);
    for (let i = 0; i < lineElements.length; i++) {
      htmls[i] = lineElements[i]!.outerHTML;
    }
    this.setupFromHTML(codeElement, htmls);
  }

  /**
   * Setup from a concatenated HTML string — splits into per-element strings.
   */
  setupFromHTMLString(codeElement: HTMLElement, htmlContent: string): void {
    const lineHTMLs = splitHTMLIntoElements(htmlContent);
    this.setupFromHTML(codeElement, lineHTMLs);
  }

  /**
   * FAST PATH: Setup directly from HAST AST nodes.
   *
   * This skips both:
   * - toHtml() on the full AST (saves ~500ms for 5000 nodes)
   * - splitHTMLIntoElements() (saves ~200ms on 5MB string)
   *
   * HAST nodes are stored raw and converted to HTML lazily — only when
   * they enter the visible viewport. For 5000 lines with 60 visible,
   * only 60 toHtml() calls happen instead of 5000.
   */
  setupFromHAST(
    codeElement: HTMLElement,
    hastChildren: ElementContent[]
  ): void {
    this.cleanUp();

    this.totalLines = hastChildren.length;
    this.container = codeElement;

    if (!this.shouldVirtualize(this.totalLines)) {
      // Below threshold: convert all to HTML and render directly
      this.isVirtualized = false;
      codeElement.innerHTML = toHtml(hastChildren);
      return;
    }

    this.isVirtualized = true;

    // Store HAST nodes for lazy conversion
    this.hastNodes = new Array(hastChildren.length);
    this.lineHTMLs = new Array(hastChildren.length);
    for (let i = 0; i < hastChildren.length; i++) {
      this.hastNodes[i] = hastChildren[i]!;
      this.lineHTMLs[i] = null; // Will be converted lazily
    }

    this.totalContentHeight = this.totalLines * this.options.lineHeight;
    this.activateVirtualization(codeElement);
  }

  /**
   * Core setup from an array of HTML strings (one per line/element).
   */
  private setupFromHTML(
    codeElement: HTMLElement,
    lineHTMLs: string[]
  ): void {
    this.cleanUp();

    this.totalLines = lineHTMLs.length;
    this.container = codeElement;

    if (!this.shouldVirtualize(this.totalLines)) {
      this.isVirtualized = false;
      codeElement.innerHTML = lineHTMLs.join('');
      return;
    }

    this.isVirtualized = true;
    this.lineHTMLs = lineHTMLs;
    this.hastNodes = [];
    this.totalContentHeight = this.totalLines * this.options.lineHeight;
    this.activateVirtualization(codeElement);
  }

  /**
   * Common virtualization activation (spacers, listeners, initial render).
   */
  private activateVirtualization(codeElement: HTMLElement): void {
    this.contentWrapper = document.createElement('div');
    this.contentWrapper.dataset.virtualizedContent = '';
    this.contentWrapper.style.cssText = 'display: contents;';

    this.spacerTop = document.createElement('div');
    this.spacerTop.dataset.virtualizedSpacerTop = '';
    this.spacerTop.style.cssText =
      'grid-column: 1 / -1; height: 0px; pointer-events: none;';

    this.spacerBottom = document.createElement('div');
    this.spacerBottom.dataset.virtualizedSpacerBottom = '';
    this.spacerBottom.style.cssText = `grid-column: 1 / -1; height: ${this.totalContentHeight}px; pointer-events: none;`;

    codeElement.innerHTML = '';
    codeElement.appendChild(this.spacerTop);
    codeElement.appendChild(this.contentWrapper);
    codeElement.appendChild(this.spacerBottom);

    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, {
      passive: true,
      capture: true,
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.measureContainerPosition();
      this.updateVisibleLines();
    });
    this.resizeObserver.observe(codeElement);

    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        this.measureContainerPosition();
        this.updateVisibleLines();
      }
    });
    this.intersectionObserver.observe(codeElement);

    requestAnimationFrame(() => {
      this.measureContainerPosition();
      this.updateVisibleLines();
    });
  }

  private handleScroll(): void {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateVisibleLines();
    });
  }

  private calculateVisibleRange(): VisibleRange {
    if (!this.container) {
      return { start: 0, end: this.totalLines };
    }

    const { lineHeight, overscan } = this.options;
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;

    const relativeScroll = scrollY - this.containerPageTop;
    const firstVisibleLine =
      relativeScroll > 0 ? Math.floor(relativeScroll / lineHeight) : 0;
    const linesInViewport = Math.ceil(viewportHeight / lineHeight);
    const lastVisibleLine = firstVisibleLine + linesInViewport;

    return {
      start: Math.max(0, firstVisibleLine - overscan),
      end: Math.min(this.totalLines, lastVisibleLine + overscan),
    };
  }

  /**
   * Get the HTML string for a line, converting from HAST lazily if needed.
   */
  private getLineHTML(index: number): string {
    let html = this.lineHTMLs[index];
    if (html !== null && html !== undefined) return html;

    // Lazy conversion from HAST node
    const node = this.hastNodes[index];
    if (node) {
      html = toHtml(node);
      this.lineHTMLs[index] = html;
      this.hastNodes[index] = null; // Free the HAST node
      return html;
    }

    return '';
  }

  /**
   * Batch innerHTML update — 1 DOM operation per frame.
   * Lazily converts HAST nodes to HTML only for visible lines.
   */
  private updateVisibleLines(): void {
    if (
      !this.isVirtualized ||
      !this.contentWrapper ||
      !this.spacerTop ||
      !this.spacerBottom
    ) {
      return;
    }

    const newRange = this.calculateVisibleRange();

    if (
      newRange.start === this.renderedRange.start &&
      newRange.end === this.renderedRange.end
    ) {
      return;
    }

    const { lineHeight } = this.options;

    this.spacerTop.style.height = `${newRange.start * lineHeight}px`;
    this.spacerBottom.style.height = `${Math.max(0, this.totalContentHeight - newRange.end * lineHeight)}px`;

    // Build HTML for visible lines — lazy HAST→HTML conversion happens here
    let html = '';
    for (let i = newRange.start; i < newRange.end; i++) {
      html += this.getLineHTML(i);
    }
    this.contentWrapper.innerHTML = html;

    this.renderedRange = newRange;
  }

  getVisibleRange(): VisibleRange {
    return { ...this.renderedRange };
  }

  scrollToLine(lineNumber: number): void {
    if (!this.container) return;
    const { lineHeight } = this.options;
    window.scrollTo({
      top: this.containerPageTop + (lineNumber - 1) * lineHeight,
      behavior: 'auto',
    });
  }

  cleanUp(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, {
        capture: true,
      } as EventListenerOptions);
      this.scrollHandler = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.container = null;
    this.contentWrapper = null;
    this.spacerTop = null;
    this.spacerBottom = null;
    this.lineHTMLs = [];
    this.hastNodes = [];
    this.totalLines = 0;
    this.totalContentHeight = 0;
    this.containerPageTop = 0;
    this.renderedRange = { start: 0, end: 0 };
    this.isVirtualized = false;
  }
}

/**
 * Split a concatenated HTML string into per-element strings.
 */
export function splitHTMLIntoElements(html: string): string[] {
  const result: string[] = [];
  let i = 0;
  const len = html.length;

  while (i < len) {
    // Skip whitespace
    while (
      i < len &&
      (html[i] === ' ' ||
        html[i] === '\n' ||
        html[i] === '\r' ||
        html[i] === '\t')
    ) {
      i++;
    }
    if (i >= len) break;

    if (html[i] !== '<') {
      const nextTag = html.indexOf('<', i);
      if (nextTag === -1) break;
      i = nextTag;
      continue;
    }

    const elementStart = i;
    let depth = 0;
    let inTag = false;
    let inQuote: string | null = null;

    while (i < len) {
      const ch = html[i];

      if (inQuote) {
        if (ch === inQuote) inQuote = null;
        i++;
        continue;
      }

      if (inTag) {
        if (ch === '"' || ch === "'") {
          inQuote = ch;
        } else if (ch === '>') {
          inTag = false;
          if (html[i - 1] === '/') depth--;
          if (depth === 0) {
            i++;
            break;
          }
        }
        i++;
        continue;
      }

      if (ch === '<') {
        if (html[i + 1] === '/') {
          depth--;
          const closeEnd = html.indexOf('>', i);
          if (closeEnd === -1) {
            i = len;
            break;
          }
          i = closeEnd + 1;
          if (depth === 0) break;
          continue;
        } else if (html[i + 1] === '!') {
          const commentEnd = html.indexOf('-->', i);
          if (commentEnd === -1) {
            i = len;
            break;
          }
          i = commentEnd + 3;
          continue;
        } else {
          depth++;
          inTag = true;
          i++;
          continue;
        }
      }

      i++;
    }

    if (i > elementStart) {
      const elementHTML = html.slice(elementStart, i);
      if (elementHTML.length > 0) {
        result.push(elementHTML);
      }
    }
  }

  return result;
}

export function extractLineElements(htmlString: string): HTMLElement[] {
  const template = document.createElement('template');
  template.innerHTML = htmlString;

  const lines: HTMLElement[] = [];
  const elements = template.content.querySelectorAll('[data-line]');

  for (const element of elements) {
    if (element instanceof HTMLElement) {
      lines.push(element);
    }
  }

  return lines;
}

export interface ParsedCodeContent {
  lines: HTMLElement[];
  separators: HTMLElement[];
  allElements: HTMLElement[];
}

export function parseCodeContent(htmlString: string): ParsedCodeContent {
  const template = document.createElement('template');
  template.innerHTML = htmlString;

  const lines: HTMLElement[] = [];
  const separators: HTMLElement[] = [];
  const allElements: HTMLElement[] = [];

  for (const child of template.content.children) {
    if (!(child instanceof HTMLElement)) continue;

    allElements.push(child);

    if ('line' in child.dataset) {
      lines.push(child);
    } else if ('separator' in child.dataset || 'buffer' in child.dataset) {
      separators.push(child);
    } else {
      lines.push(child);
    }
  }

  return { lines, separators, allElements };
}
